/**
 * 预订修改
 */
import React, { useEffect, memo } from 'react';
import { connect } from 'react-redux';
import { message, Spin, Button } from 'antd';
import intl from 'react-intl-universal';

import usePortalDrawer, { DrawerHeader, DrawerFooter } from '@/components/PortalDrawer';
import PortalLoading from '@/components/PortalLoading';
import PopupTarget, { TargetType } from '@/components/Reservation/PopupTarget';
import ThrottleButton from '@/components/Reservation/ThrottleButton';
import usePortalConfirm from '@/components/PortalConfirm';
import * as EditService from '@/api/orderEdit';
import DateJS from '@/utils/date';

import { createStateSelector } from '@/redux/actions/reservation/helper';
import {
  initPageState,
  setLoadingStatus,
  getMainState,
} from '@/redux/actions/reservation/reserveEditor';
import { ReducerState, storeName } from '@/redux/reducers/reservation/reserveEditor';

import RentTimeConfig from './components/RentTimeInfo';
import ChannelConfig from './components/ChannelInfo';
import RentRoomList from './components/RentRoomList';
import RemarkInfo from './components/RemarkInfo';
import validator from './validator';
import * as helper from './helper';

import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { isInit, isLoading } = state;
  return { isInit, isLoading };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {
  callInitState: initPageState,
  callSetLoading: setLoadingStatus,
  callGetState: getMainState,
};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

interface IConfig {
  recList: Array<string>; // 接待单列表
  onEnd?: (result: boolean) => void;
}
interface IProps extends IConfig, MapStateProps, MapDispatchProps {
  unmountNode: () => void;
}

const Index: React.FC<IProps> = (props) => {
  const { unmountNode, onEnd, isInit, isLoading, callSetLoading, callGetState } = props;
  const confirmPortal = usePortalConfirm();

  useEffect(() => {
    const { callInitState, recList } = props;
    if (recList && recList.length) {
      // 初始化数据
      callInitState(() => {
        return helper.getStateFromOrder(recList).catch((e) => {
          unmountNode();
          throw e;
        });
      });
    } else {
      unmountNode();
      message.error(intl.get('reservation_error_config_invalid'));
    }
  }, []);

  const closeDrawer = (result: boolean) => {
    unmountNode();
    if (typeof onEnd === 'function') {
      onEnd(result);
    }
  };

  // 取消
  const handleEditCancel = () => {
    closeDrawer(false);
  };

  // 预抵时间修改确认
  const confirmCheckETATime = async () => {
    return new Promise<void>((resolve, reject) => {
      callGetState(async (state) => {
        const { commonInfo, originBaseInfo, rentTimeInfo } = state;
        // 直连渠道 且 预抵时间不一样
        if (
          commonInfo.isDirectChannel &&
          !DateJS(originBaseInfo.expectedArrivalTime).isSame(rentTimeInfo.expectedArrivalTime)
        ) {
          confirmPortal.showDrawer({
            title: intl.get('drawer_order_editor_direct_time_title'),
            content: intl.get('drawer_order_editor_direct_time_content'),
            cancelText: intl.get('app_confirm_cancel_btn'),
            onCancel: () => {
              confirmPortal.closeDrawer();
              reject();
            },
            confirmText: intl.get('drawer_order_editor_direct_time_next_btn'),
            onConfirm: () => {
              confirmPortal.closeDrawer();
              resolve();
            },
          });
        } else {
          resolve();
        }
      });
    });
  };

  // 修改提交
  const handleSubmitClick = async () => {
    callGetState(async (state) => {
      const err = validator(state);
      if (err) {
        message.error(err);
        return;
      }
      try {
        callSetLoading(true);
        await confirmCheckETATime();
        const requestData = helper.formatToSubmitData(state);
        await EditService.modifyReserveOrder(requestData);
        closeDrawer(true);
        message.success(intl.get('drawer_order_editor_message_update_success'));
      } catch (e) {
        callSetLoading(false);
      }
    });
  };

  if (!isInit) return <PortalLoading />;

  return (
    <Spin spinning={isLoading}>
      <DrawerHeader
        title={intl.get('drawer_order_editor_reserve_title')}
        onClose={handleEditCancel}
      />
      <div className={styles.pageContainer}>
        <div className={styles.mainContent}>
          <PopupTarget id={TargetType.ReserveEditor} />
          <RentTimeConfig />
          <ChannelConfig />
          <RentRoomList />
          <RemarkInfo />
        </div>
      </div>
      <DrawerFooter>
        <ThrottleButton type="primary" className={styles.submitBtn} onClick={handleSubmitClick}>
          {intl.get('drawer_order_editor_submit_btn')}
        </ThrottleButton>
        <Button onClick={handleEditCancel}>{intl.get('app_confirm_cancel_btn')}</Button>
      </DrawerFooter>
    </Spin>
  );
};

export default () =>
  usePortalDrawer<IConfig>(connect(mapStateToProps, mapDispatchToProps)(memo(Index)));
